import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarked, FaUsers, FaHistory, FaLightbulb, FaBullseye, FaHandshake } from 'react-icons/fa';
import SEO from '../components/seo/SEO';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO
        pageType="static"
        title="About Us - Pincode Pro"
        description="Learn about Pincode Pro, India's most comprehensive pincode search service. Discover our mission, features, and commitment to accurate postal information."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              About Pincode Pro
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-50">
              Empowering India with Accurate Postal Information Since 2020
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Our Mission</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              At Pincode Pro, we're dedicated to providing the most comprehensive and accurate postal code information 
              system in India. Our mission is to simplify the process of finding postal information while ensuring 
              reliability and accessibility for everyone - from individual users to large enterprises.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 rounded-lg bg-blue-50"
              >
                <FaBullseye className="text-blue-500 text-3xl mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Vision</h3>
                <p className="text-gray-600">
                  To become India's most trusted source for postal information, making accurate address verification 
                  accessible to all citizens and businesses.
                </p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 rounded-lg bg-teal-50"
              >
                <FaLightbulb className="text-teal-500 text-3xl mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Innovation</h3>
                <p className="text-gray-600">
                  Continuously improving our services with cutting-edge technology to provide faster, more accurate, 
                  and more reliable postal information.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* History & Growth Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Our Journey</h2>
            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <FaHistory className="text-blue-500 text-3xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Established in 2020</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Started with a vision to digitize India's postal information system and make it accessible to everyone. 
                    Within the first year, we covered over 100,000 post offices across India.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <FaUsers className="text-blue-500 text-3xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">Growing Community</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Today, we serve millions of users monthly, from individual citizens to large enterprises, 
                    helping them find accurate postal information instantly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Coverage */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Services & Coverage</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <FaMapMarked className="text-blue-500 text-3xl mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Nationwide Coverage</h3>
                <ul className="text-gray-600 space-y-2">
                  <li>• Complete coverage of all Indian states</li>
                  <li>• 155,000+ post offices indexed</li>
                  <li>• Regular updates from India Post</li>
                  <li>• Detailed district and state mapping</li>
                </ul>
              </div>
              <div>
                <FaHandshake className="text-blue-500 text-3xl mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Enterprise Solutions</h3>
                <ul className="text-gray-600 space-y-2">
                  <li>• API access for businesses</li>
                  <li>• Bulk lookup capabilities</li>
                  <li>• Address verification services</li>
                  <li>• Custom integration support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Experience the Difference</h2>
          <p className="text-xl mb-8 text-blue-50">Join millions of users who trust Pincode Pro for accurate postal information</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold shadow-lg hover:bg-blue-50 transition-colors"
            onClick={() => window.location.href = '/'}
          >
            Try Pincode Pro
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default About;
